import { ReactNode, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePartnerConfig } from '@percent/hosted-validation-form/hooks';
import { ProgressContextController } from '@percent/hosted-validation-form/context/progress/ProgressContextController/ProgressContextController';
import { HostedFormProps } from '../HostedForm.types';
import { useNavigationStack } from '@percent/hosted-validation-form/hooks/useNavigationStack/useNavigationStack';
import { ErrorCard } from '@percent/hosted-validation-form/ui/ErrorCard/ErrorCard';
import { ErrorVariant } from '@percent/domain/verifications';
import { Loader } from '@percent/lemonade';

import { HostedFormStep } from './HostedForm.types';
import {
  OrganisationOrigin,
  SelectOrganisationContainer,
  OrganisationResult,
  ContactDetails,
  Email,
  Otp,
  TermsContainer,
  Complete,
  Website,
  RegisterOrganisationContainer,
  OrganisationAddress,
  PartnerFields,
} from './steps';

const steps: { [key in HostedFormStep]: ReactNode } = {
  [HostedFormStep.PARTNER_FIELDS]: <PartnerFields />,
  [HostedFormStep.ORGANISATION_ORIGIN]: <OrganisationOrigin />,
  [HostedFormStep.SELECT_ORGANISATION]: <SelectOrganisationContainer />,
  [HostedFormStep.REGISTER_ORGANISATION]: <RegisterOrganisationContainer />,
  [HostedFormStep.ORGANISATION_ADDRESS]: <OrganisationAddress />,
  [HostedFormStep.ORGANISATION_RESULT]: <OrganisationResult />,
  [HostedFormStep.CONTACT_DETAILS]: <ContactDetails />,
  [HostedFormStep.WEBSITE]: <Website />,
  [HostedFormStep.EMAIL]: <Email />,
  [HostedFormStep.OTP]: <Otp />,
  [HostedFormStep.TERMS]: <TermsContainer />,
  [HostedFormStep.COMPLETE]: <Complete />,
};

export const HostedForm = ({ isError, isInternalError, isLoading, isBlockedCookies }: HostedFormProps) => {
  const { id } = useParams();
  const { partnerConfig } = usePartnerConfig();
  const { validationStep } = useNavigationStack();
  const { t, i18n } = useTranslation();
  const methods = useFormContext();

  useEffect(() => {
    if (i18n.language) {
      return methods.setValue('language', i18n.language);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    methods.setValue('validationInviteId', id);
  }, [id]);

  return (
    <ProgressContextController>
      {isLoading && !isError && !isInternalError && <Loader />}
      {!isLoading && partnerConfig?.status === 'complete' && (
        <ErrorCard
          title={t('hostedValidation.errorMessage.linkUsedTitle')}
          subtitle={t('hostedValidation.errorMessage.linkUsedSubtitle')}
          variant={ErrorVariant.LINK_ALREADY_USED}
        />
      )}
      {!isLoading && isInternalError && !isError && !isBlockedCookies && (
        <ErrorCard
          title={t('hostedValidation.errorMessage.somethingWentWrong')}
          subtitle={t('hostedValidation.errorMessage.reloadPage')}
          variant={ErrorVariant.INTERNAL}
        />
      )}
      {!isLoading && isError && !isInternalError && !isBlockedCookies && (
        <ErrorCard
          variant={ErrorVariant.WRONG_LINK}
          title={t('hostedValidation.errorMessage.invalidLink')}
          subtitle={t('hostedValidation.errorMessage.linkDoesntExist')}
        />
      )}
      {!isLoading && !isError && !isInternalError && isBlockedCookies && (
        <ErrorCard
          variant={ErrorVariant.BLOCKED_COOKIES}
          title={t('hostedValidation.errorMessage.blockedCookies')}
          subtitle={t('hostedValidation.errorMessage.allowCookies')}
        />
      )}
      {!isLoading && partnerConfig?.status === 'open' && !isError && steps[validationStep]}
    </ProgressContextController>
  );
};
